import { useState, useEffect, useRef } from "react";

type Props = {
  title: string,
  backgroundClass: string,
  digitClass: string,
};

const CountdownTimer = ({
  title,
  backgroundClass,
  digitClass,
}: Props) => {
  const COUNTER_RESET_MS = 2 * 24 * 60 * 60 * 1000;
  const DATA_KEY = "countdownData";
  const startValues = [1562, 1678, 1735];

  const getRandomStartValue = () =>
    startValues[Math.floor(Math.random() * startValues.length)];

  const getRandomStopValue = () =>
    Math.floor(Math.random() * (23 - 12 + 1)) + 12;

  const getRandomDecrement = () =>
    Math.floor(Math.random() * (6 - 2 + 1)) + 2;

  const getRandomInterval = () =>
    Math.floor(Math.random() * (5 - 2 + 1)) + 2;

  const [startValue, setStartValue] = useState<number | null>(null);
  const [stopValue, setStopValue] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [hasStarted, setHasStarted] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Load or initialize data
  useEffect(() => {
  const now = Date.now();
  const saved = localStorage.getItem(DATA_KEY);

  let shouldReset = true;

  if (saved) {
    const data = JSON.parse(saved);
    const firstStart = data.startTimestamp ?? now;

    // Check if 3 days have NOT passed
    if (now - firstStart < COUNTER_RESET_MS) {
      setStartValue(data.startValue);
      setStopValue(data.stopValue);
      setCount(data.count);
      shouldReset = false; // ✅ Don't reset
    }
  }

  if (shouldReset) {
    const newStart = getRandomStartValue();
    const newStop = getRandomStopValue();
    const newCount = Math.floor(newStart / 10) * 2;

    setStartValue(newStart);
    setStopValue(newStop);
    setCount(newCount);

    localStorage.setItem(
      DATA_KEY,
      JSON.stringify({
        startValue: newStart,
        stopValue: newStop,
        count: newCount,
        startTimestamp: now,
      })
    );
  }
}, []);

  // Setup IntersectionObserver to trigger countdown on view
  useEffect(() => {
    if (!containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      if (observerRef.current && containerRef.current) {
        observerRef.current.unobserve(containerRef.current);
      }
    };
  }, [hasStarted]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = localStorage.getItem(DATA_KEY);
      if (!data) return;

      const parsed = JSON.parse(data);
      const now = Date.now();

      if (parsed.startTimestamp && now - parsed.startTimestamp >= COUNTER_RESET_MS) {
        const newStart = getRandomStartValue();
        const newStop = getRandomStopValue();
        const newCount = Math.floor(newStart / 10) * 2;

        setStartValue(newStart);
        setStopValue(newStop);
        setCount(newCount);

        localStorage.setItem(
          DATA_KEY,
          JSON.stringify({
            startValue: newStart,
            stopValue: newStop,
            count: newCount,
            startTimestamp: now, // reset timestamp to now
          })
        );
      }
    }, 10 * 1000); // check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Countdown loop
  useEffect(() => {
    if (!hasStarted || count === null || stopValue === null || count <= stopValue) {
      return;
    }

    const run = () => {
      const decrement = getRandomDecrement();
      const next = count - decrement;
      const final = next <= stopValue ? stopValue : next;

      setCount(final);

      const existingData = localStorage.getItem(DATA_KEY);
      const preservedTimestamp = existingData
        ? JSON.parse(existingData).startTimestamp ?? Date.now()
        : Date.now();

      localStorage.setItem(
        DATA_KEY,
        JSON.stringify({
          startValue,
          stopValue,
          count: final,
          startTimestamp: preservedTimestamp, // ✅ always preserved
        })
      );

      if (final > stopValue) {
        const interval = getRandomInterval();
        timerRef.current = setTimeout(run, interval * 1000);
      }
    };

    const initialDelay = getRandomInterval();
    timerRef.current = setTimeout(run, initialDelay * 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [hasStarted, count, stopValue, startValue]);

  return (
    <section id="countdown">
      <div ref={containerRef} className={`container-fluid pt-3 ${backgroundClass}`}>
        <div className="row justify-content-center align-items-center text-center">
          <div className="col-9 text-center">
            <h1 dangerouslySetInnerHTML={{ __html: title }}></h1>
            <p className="mt-4">Limited stock available: </p>
            {count?.toString().padStart(5, '0').split('').map((digit, index) => (
              <span key={index} className={`align-items-center justify-content-center d-inline py-2 px-2 fw-bold fs-3 mx-1 text-white ${digitClass}`}>{digit}</span>
            ))}
            <p className="mt-3">out of {startValue} bottles released a few days ago.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownTimer;
